import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants';
import './SetPasswordPage.css';

const tokenOk = (responseText: string) => {
  // TODO: Handle a valid password-reset token.
  // Use: https://rivident.com/set-password?token=...
  console.info('Password-reset token accepted', responseText);
};

const badToken = (responseText?: string) => {
  // TODO: Handle an invalid or missing password-reset token.
  console.error('Password-reset token rejected', responseText);
};

export function SetPasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [repeatedPassword, setRepeatedPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatedPassword, setShowRepeatedPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const tokenWasChecked = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (tokenWasChecked.current) {
      return;
    }
    tokenWasChecked.current = true;

    const token = new URLSearchParams(window.location.search).get('token')?.trim();

    console.log('Password-reset code:', token);

    if (!token) {
      badToken();
      return;
    }

    localStorage.setItem('activationCode', token);

    const checkToken = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/password-reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });
        const responseText = await response.text();

        if (responseText.startsWith('ok ')) {
          tokenOk(responseText);
        } else {
          badToken(responseText);
        }
      } catch (error) {
        console.error('Password-reset token could not be checked', error);
        badToken();
      }
    };

    void checkToken();
  }, []);

  const hasMinimumLength = newPassword.length >= 8;
  const hasUpperAndLowerCase = /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword);
  const hasDigit = /\d/.test(newPassword);

  const setPassword = async () => {
    const email = localStorage.getItem('forgottenPasswordEmail') ?? '';
    //const email = localStorage.getItem('activationEmail') ?? '';
    const tempPassword = localStorage.getItem('activationCode') ?? '';

    setIsSaving(true);
    setErrorMessage('');

    try {
      console.log("/user/activate email=" + email + " tempPassword=" + tempPassword + " newPassword=" + newPassword);

      const response = await fetch(`${API_BASE_URL}/user/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, tempPassword, password: newPassword }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          setErrorMessage('User not found or bad code');
        } else {
          setErrorMessage('Das Passwort konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
        }
        return;
      }

      localStorage.removeItem('activationEmail');
      localStorage.removeItem('activationCode');
      navigate('/login');
    } catch (error) {
      console.error('Failed to activate user', error);
      setErrorMessage('Das Passwort konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSaving(false);
    }
  };

  const savePassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!hasMinimumLength || !hasUpperAndLowerCase || !hasDigit) {
      setErrorMessage(
        'Das Passwort muss mindestens 8 Zeichen, Gross- und Kleinbuchstaben sowie mindestens eine Zahl enthalten.',
      );
      return;
    }

    if (newPassword !== repeatedPassword) {
      setErrorMessage('Die eingegebenen Passwörter stimmen nicht überein.');
      return;
    }

    await setPassword();
  };

  const PasswordIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
      <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
      <path d="M3 3l18 18M10.6 6.2A11 11 0 0 1 12 6c6 0 9.5 6 9.5 6a16 16 0 0 1-3 3.6M6.2 6.2A17 17 0 0 0 2.5 12s3.5 6 9.5 6a10 10 0 0 0 3.1-.5M9.9 9.9a3 3 0 0 0 4.2 4.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <main
      className="set-password-page"
      style={{ '--set-password-background-image': "url('/images/StartImage.jpg')" } as React.CSSProperties}
    >
      <section className="set-password-page__card">
        <button
          type="button"
          onClick={() => navigate('/registration-code')}
          className="set-password-page__back"
          aria-label="Zurück zur Registrierung"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Zurück
        </button>

        <div className="set-password-page__header">
          <div className="set-password-page__badge">
            <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" aria-hidden="true">
              <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <circle cx="12" cy="15" r="1" fill="currentColor" />
            </svg>
          </div>
          <h1 className="set-password-page__title">
            Neues Passwort festlegen
          </h1>
        </div>

        <p className="set-password-page__description">
          Bitte legen Sie jetzt Ihr persönliches Passwort fest. Geben Sie es zur Bestätigung zweimal ein.
        </p>

        <form className="set-password-page__form" onSubmit={savePassword} noValidate>
          <label htmlFor="new-password" className="set-password-page__label">
            Neues Passwort
          </label>
          <div className="set-password-page__field">
            <span className="set-password-page__field-icon"><PasswordIcon /></span>
            <input
              id="new-password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder="Mindestens 8 Zeichen"
              className="set-password-page__input"
            />
            <button type="button" onClick={() => setShowNewPassword((value) => !value)} className="set-password-page__toggle" aria-label={showNewPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}>
              <EyeOffIcon />
            </button>
          </div>

          <label htmlFor="repeated-password" className="set-password-page__label set-password-page__label--repeat">
            Neues Passwort bestätigen
          </label>
          <div className="set-password-page__field">
            <span className="set-password-page__field-icon"><PasswordIcon /></span>
            <input
              id="repeated-password"
              type={showRepeatedPassword ? 'text' : 'password'}
              value={repeatedPassword}
              onChange={(event) => setRepeatedPassword(event.target.value)}
              placeholder="Passwort wiederholen"
              className="set-password-page__input"
            />
            <button type="button" onClick={() => setShowRepeatedPassword((value) => !value)} className="set-password-page__toggle" aria-label={showRepeatedPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}>
              <EyeOffIcon />
            </button>
          </div>

          <aside className="set-password-page__requirements">
            <p className="set-password-page__requirements-title">Passwortanforderungen:</p>
            <p>○&nbsp; Mindestens 8 Zeichen</p>
            <p>○&nbsp; Gross- und Kleinbuchstaben</p>
            <p>○&nbsp; Mindestens eine Zahl</p>
          </aside>

          <button type="submit" disabled={isSaving} className="set-password-page__submit">
            {isSaving ? 'Passwort wird gespeichert …' : 'Passwort speichern'}
          </button>
        </form>
      </section>

      {errorMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-5" role="presentation" onMouseDown={() => setErrorMessage('')}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl" role="alertdialog" aria-modal="true" aria-labelledby="password-error-title" aria-describedby="password-error-message" onMouseDown={(event) => event.stopPropagation()}>
            <h2 id="password-error-title" className="text-xl font-semibold text-slate-900">Passwort ungültig</h2>
            <p id="password-error-message" className="mt-3 text-sm leading-6 text-slate-600">{errorMessage}</p>
            <button type="button" autoFocus onClick={() => setErrorMessage('')} className="mt-6 h-11 w-full rounded-[10px] bg-[#d0bc93] font-medium text-white hover:bg-[#bda574] focus:outline-none focus:ring-2 focus:ring-[#cbb58a] focus:ring-offset-2">OK</button>
          </div>
        </div>
      )}
    </main>
  );
}
